import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  getBrokerHoldingBookColumns,
  getBrokerPositionBookColumns,
  getBrokerOrderBookColumns,
} from "../../api/brokerBooks";
import { getBulkOrderColumns } from "../../api/bulkOrder";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { extractColumnsConfig } from "../../utils/brokerBookColumns";
import {
  TABLE_COLUMN_TABLE_IDS,
  clearColumnKeys,
  getSavedColumnKeys,
  moveKeyInList,
  resolveDisplayColumnKeys,
  saveColumnKeys,
} from "../../utils/tableColumnPreferences";
import styles from "./TableColumnPreferencesSection.module.css";

const STANDALONE_TABLE_OPTIONS = [
  {
    id: TABLE_COLUMN_TABLE_IDS.HOLDING_BOOK,
    label: "Holding Book",
    iconName: "HoldingBookIcon",
    fetchColumns: getBrokerHoldingBookColumns,
  },
  {
    id: TABLE_COLUMN_TABLE_IDS.POSITION_BOOK,
    label: "Position Book",
    iconName: "PositionBookIcon",
    fetchColumns: getBrokerPositionBookColumns,
  },
];

const ORDER_BOOK_SUB_OPTIONS = [
  {
    id: TABLE_COLUMN_TABLE_IDS.ORDER_BOOK,
    label: "Order Book",
    iconName: "OrderBookIcon",
    fetchColumns: getBrokerOrderBookColumns,
  },
  {
    id: TABLE_COLUMN_TABLE_IDS.BULK_ORDER,
    label: "Bulk Order",
    iconName: "BulkOrderIcon",
    fetchColumns: getBulkOrderColumns,
  },
];

const ALL_TABLE_OPTIONS = [...STANDALONE_TABLE_OPTIONS, ...ORDER_BOOK_SUB_OPTIONS];

const ORDER_BOOK_TABLE_IDS = new Set(
  ORDER_BOOK_SUB_OPTIONS.map((opt) => opt.id)
);

export default function TableColumnPreferencesSection() {
  const [selectedTableId, setSelectedTableId] = useState(STANDALONE_TABLE_OPTIONS[0].id);
  const [apiColumns, setApiColumns] = useState([]);
  const [orderedKeys, setOrderedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const selectedOption = useMemo(
    () => ALL_TABLE_OPTIONS.find((o) => o.id === selectedTableId) ?? STANDALONE_TABLE_OPTIONS[0],
    [selectedTableId]
  );

  const isOrderBookGroup = ORDER_BOOK_TABLE_IDS.has(selectedTableId);

  const columnByKey = useMemo(() => {
    const map = new Map();
    for (const col of apiColumns) {
      if (col?.key) map.set(col.key, col);
    }
    return map;
  }, [apiColumns]);

  const clearDragState = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  const loadColumns = useCallback(async () => {
    setLoading(true);
    setError(null);
    clearDragState();
    try {
      const response = await selectedOption.fetchColumns();
      const columns = extractColumnsConfig(response);
      if (!columns?.length) {
        setApiColumns([]);
        setOrderedKeys([]);
        setError("No columns returned from the server.");
        return;
      }
      setApiColumns(columns);
      setOrderedKeys(resolveDisplayColumnKeys(columns, selectedTableId));
    } catch (err) {
      const message =
        err?.response?.data?.detail || err?.message || "Failed to load column configuration";
      setError(message);
      setApiColumns([]);
      setOrderedKeys([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOption, selectedTableId, clearDragState]);

  useEffect(() => {
    void loadColumns();
  }, [loadColumns]);

  const applyReorder = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;
      setOrderedKeys((prev) => {
        const next = moveKeyInList(prev, fromIndex, toIndex);
        saveColumnKeys(selectedTableId, next);
        return next;
      });
    },
    [selectedTableId]
  );

  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    setDragOverIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  }, []);

  const handleDragOver = useCallback(
    (e, index) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragIndex !== null && dragIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [dragIndex]
  );

  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData("text/plain");
      const fromIndex = dragIndex ?? Number(raw);
      if (!Number.isFinite(fromIndex) || fromIndex === dropIndex) {
        clearDragState();
        return;
      }
      applyReorder(fromIndex, dropIndex);
      clearDragState();
    },
    [dragIndex, applyReorder, clearDragState]
  );

  const handleDragEnd = useCallback(() => {
    clearDragState();
  }, [clearDragState]);

  const handleReset = useCallback(() => {
    clearColumnKeys(selectedTableId);
    setOrderedKeys(apiColumns.map((c) => c.key).filter(Boolean));
    clearDragState();
  }, [selectedTableId, apiColumns, clearDragState]);

  const hasCustomOrder = Boolean(getSavedColumnKeys(selectedTableId)?.length);

  return (
    <div className={styles.section}>
      <div className={styles.contentHeader}>
        <h1 className={styles.contentTitle}>Preferences</h1>
        <p className={styles.contentSubtitle}>
          Choose the column order for Holding, Position, and Order Book (Order Book and Bulk Order
          tabs).
        </p>
      </div>

      <div className={styles.tablePills} role="tablist" aria-label="Select book">
        {STANDALONE_TABLE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={selectedTableId === opt.id}
            className={`${styles.tablePill} ${selectedTableId === opt.id ? styles.tablePillActive : ""}`}
            onClick={() => setSelectedTableId(opt.id)}
          >
            <GlobalIcon iconName={opt.iconName} className={styles.tablePillIcon} />
            {opt.label}
          </button>
        ))}
      </div>

      <div className={styles.orderBookGroup}>
        <div className={styles.orderBookGroupLabel}>
          <GlobalIcon iconName="OrderBookIcon" className={styles.tablePillIcon} />
          Order Book
        </div>
        <div
          className={styles.orderBookSubPills}
          role="tablist"
          aria-label="Order book tabs"
        >
          {ORDER_BOOK_SUB_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={selectedTableId === opt.id}
              className={`${styles.tablePill} ${styles.orderBookSubPill} ${
                selectedTableId === opt.id ? styles.tablePillActive : ""
              }`}
              onClick={() => setSelectedTableId(opt.id)}
            >
              <GlobalIcon iconName={opt.iconName} className={styles.tablePillIcon} />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.toolbar}>
        <p className={styles.toolbarHint}>
          Drag rows up or down to reorder columns. Changes apply immediately to{" "}
          {isOrderBookGroup ? `Order Book — ${selectedOption.label}` : selectedOption.label}.
        </p>
        <Button
          type="button"
          variant="outlined"
          size="small"
          className={styles.resetBtn}
          onClick={handleReset}
          disabled={loading || !apiColumns.length || !hasCustomOrder}
        >
          Reset to default
        </Button>
      </div>

      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <div className={styles.columnList} aria-busy={loading}>
        {loading ? (
          <div className={styles.loading}>
            <CircularProgress size={24} sx={{ mb: 1 }} />
            <div>Loading columns…</div>
          </div>
        ) : orderedKeys.length === 0 ? (
          <div className={styles.empty}>No columns available.</div>
        ) : (
          orderedKeys.map((key, index) => {
            const col = columnByKey.get(key);
            const isDragging = dragIndex === index;
            const isDragOver = dragOverIndex === index && dragIndex !== index;
            return (
              <div
                key={key}
                className={`${styles.columnRow} ${isDragging ? styles.columnRowDragging : ""} ${
                  isDragOver ? styles.columnRowDragOver : ""
                }`}
                draggable={!loading}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                onDragLeave={() => {
                  if (dragOverIndex === index) setDragOverIndex(null);
                }}
              >
                <span
                  className={styles.dragHandle}
                  aria-hidden
                  title="Drag to reorder"
                >
                  <DragIndicatorIcon fontSize="small" />
                </span>
                <span className={styles.columnIndex}>{index + 1}</span>
                <div className={styles.columnBody}>
                  <div className={styles.columnLabel}>{col?.label || key}</div>
                  <div className={styles.columnKey}>{key}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
